import { ArrowUpRightOnBox, MapPin } from "@medusajs/icons"
import { useMessageTemplates } from "../lib/messages"
import { Badge, Button, Checkbox, clx, Container, Heading, Input, Label, Skeleton, Text, Textarea, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState, type FormEvent, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { copyText } from "../lib/clipboard"
import {
  bookingApi,
  dayLabel,
  geocodeWords,
  KIND_LABEL,
  STATUS_COLOUR,
  STATUS_LABEL,
  whenLabel,
  type BookingKind,
  type BookOnBehalfBody,
  type GeocodeCheck,
} from "../lib/booking"
import { t } from "../lib/i18n"
import { errorMessage } from "../lib/medusa"

export type BookingPrefill = {
  kind: BookingKind
  firstName: string
  lastName: string
  instagram: string
  phone: string
  email: string
  addressLine: string
  addressArea: string
  postalCode: string
}

/** Today in Athens as YYYY-MM-DD: the booking app's days are Athens days. */
export const athensToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Athens" }).format(new Date())

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-y-1">
      <Label htmlFor={id} size="small" weight="plus">{label}</Label>
      {children}
      {hint && <Text size="xsmall" className="text-ui-fg-muted">{hint}</Text>}
    </div>
  )
}

function Chip({ active, onClick, children, disabled }: { active: boolean; onClick: () => void; children: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={clx(
        "txt-compact-small-plus inline-flex h-9 shrink-0 items-center rounded-full border px-3.5 disabled:opacity-40",
        active ? "bg-ui-fg-base text-ui-bg-base border-transparent" : "bg-ui-bg-base text-ui-fg-subtle border-ui-border-base"
      )}
    >
      {children}
    </button>
  )
}

/** What Google made of the address, in words, with the pin to check on the map. */
export function GeocodeResult({ result, postalCode }: { result: GeocodeCheck; postalCode: string }) {
  const words = geocodeWords(result.status)
  const otherPostcode = result.postalCode && postalCode && result.postalCode !== postalCode.replace(/\s/g, "")
  return (
    <div className="bg-ui-bg-subtle flex flex-col gap-y-1 rounded-md p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge size="2xsmall" color={words.colour}>{words.label}</Badge>
        {result.mapUrl && (
          <a href={result.mapUrl} target="_blank" rel="noreferrer" className="txt-compact-small text-ui-fg-interactive inline-flex items-center gap-x-1">
            <MapPin /> {t("See the pin")}
          </a>
        )}
      </div>
      {result.formatted && <Text size="small">{t("Google reads it as: {address}", { address: result.formatted })}</Text>}
      {otherPostcode && (
        <Text size="small" className="text-ui-fg-error">{t("Google places it in postcode {code}, not {postal}. Check which is right.", { code: result.postalCode, postal: postalCode })}</Text>
      )}
      {result.status === "APPROXIMATE" && <Text size="xsmall" className="text-ui-fg-subtle">{t("Only the street or area was found, not the building. Check the number, and ask the customer if unsure.")}</Text>}
      {result.status === "FAILED" && <Text size="xsmall" className="text-ui-fg-subtle">{t("The route cannot include a stop Google cannot find. Fix the spelling, or ask the customer.")}</Text>}
    </div>
  )
}

/**
 * Book a customer on a day and time already arranged with them (usually in the DMs). A time or day
 * that is not published is created for this booking only: a meet-up time stays hidden from other
 * customers, a new delivery day runs with this one stop. The address is checked with Google before
 * saving, because the route and the courier go by the pin.
 */
export function BookDeliveryForm(props: {
  source: BookOnBehalfBody["source"]
  orderId?: string
  orderNumber?: string
  prefill: BookingPrefill
  onDone: (booking: { id: string; ref: string }) => void
  onCancel?: () => void
}) {
  const { source, orderId, prefill } = props
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const today = athensToday()
  const overview = useQuery({ queryKey: ["booking", "overview"], queryFn: bookingApi.overview })
  const [kind, setKind] = useState<BookingKind>(prefill.kind)
  const [person, setPerson] = useState({ firstName: prefill.firstName, lastName: prefill.lastName, instagram: prefill.instagram, phone: prefill.phone, email: prefill.email })
  const [orderNumber, setOrderNumber] = useState(props.orderNumber ?? "")
  const [meet, setMeet] = useState({ date: "", time: "" })
  const [home, setHome] = useState({ date: "", windowStart: "", windowEnd: "" })
  const [address, setAddress] = useState({ addressLine: prefill.addressLine, addressArea: prefill.addressArea, postalCode: prefill.postalCode, addressFloor: "", addressNotes: "" })
  const [avail, setAvail] = useState({ from: "", until: "", notes: "" })
  const [notify, setNotify] = useState(Boolean(prefill.email))
  const [check, setCheck] = useState<{ key: string; result: GeocodeCheck } | null>(null)

  const addressKey = `${address.addressLine.trim()}|${address.addressArea.trim()}|${address.postalCode.replace(/\s/g, "")}`
  const checked = check?.key === addressKey ? check.result : null

  const openTimes = useMemo(
    () =>
      (overview.data?.meetupDays ?? [])
        .filter((d) => d.date >= today)
        .flatMap((d) => d.slots.filter((s) => s.status === "OPEN" && s.bookings.length < s.capacity).map((s) => ({ date: d.date, time: s.time }))),
    [overview.data, today]
  )
  const homeDays = useMemo(() => (overview.data?.homeDays ?? []).filter((d) => d.date >= today && d.status !== "CANCELLED" && d.status !== "COMPLETED"), [overview.data, today])
  const existingDay = homeDays.find((d) => d.date === home.date) ?? (overview.data?.homeDays ?? []).find((d) => d.date === home.date)
  const arrangedTime = meet.date && meet.time && !openTimes.some((slot) => slot.date === meet.date && slot.time === meet.time)

  const geocode = useMutation({
    mutationFn: () => bookingApi.geocode({ addressLine: address.addressLine, addressArea: address.addressArea, postalCode: address.postalCode.replace(/\s/g, "") }),
    onSuccess: (result) => setCheck({ key: addressKey, result }),
    onError: (error) => toast.error(errorMessage(error)),
  })

  const book = useMutation({
    mutationFn: (body: BookOnBehalfBody) => bookingApi.book(body),
    onSuccess: (result) => {
      toast.success(t("Booked · {ref}", { ref: result.ref }))
      void queryClient.invalidateQueries({ queryKey: ["booking"] })
      props.onDone(result)
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const name = `${person.firstName} ${person.lastName}`.trim()
  const postal = address.postalCode.replace(/\s/g, "")
  const missing = [
    !name && t("a name"),
    !person.phone.trim() && !person.instagram.trim() && t("a phone or Instagram"),
    kind === "MEETUP" && (!meet.date || !CLOCK.test(meet.time)) && t("the day and time"),
    kind === "HOME" && !home.date && t("the delivery day"),
    kind === "HOME" && address.addressLine.trim().length < 3 && t("the street and number"),
    kind === "HOME" && !/^\d{5}$/.test(postal) && t("a 5-digit postcode"),
    source === "hq" && !orderNumber.trim() && t("the order number (or a note of what it is)"),
  ].filter(Boolean) as string[]

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (missing.length) return
    let geo = checked
    if (kind === "HOME" && !geo) {
      geo = await bookingApi.geocode({ addressLine: address.addressLine, addressArea: address.addressArea, postalCode: postal }).catch(() => null)
      if (geo) setCheck({ key: addressKey, result: geo })
    }
    const warnings: string[] = []
    if (kind === "HOME") {
      if (!geo || geo.status === "FAILED") warnings.push(t("Google could not find this address, so the route will leave it out until it is fixed."))
      else if (geo.status === "APPROXIMATE") warnings.push(t("Google found only the street or area, not the building."))
      if (geo?.postalCode && geo.postalCode !== postal) warnings.push(t("Google places it in postcode {code}, not {postal}.", { code: geo.postalCode, postal }))
    }
    const when = kind === "MEETUP" ? t("{day} at {time}", { day: dayLabel(meet.date), time: meet.time }) : dayLabel(home.date)
    const ok = await prompt({
      title: kind === "MEETUP" ? t("Book {name}: meet-up on {when}?", { name, when }) : t("Book {name}: home delivery on {when}?", { name, when }),
      description: [
        ...warnings,
        kind === "MEETUP" && arrangedTime ? t("This time is not published, so it is made for this booking only and stays hidden from other customers.") : "",
        kind === "HOME" && !existingDay ? t("There is no delivery day on that date yet, so one is made for this booking only: it runs with this stop and customers do not see it.") : "",
        notify && person.email.trim() ? t("{email} is emailed the booking.", { email: person.email.trim() }) : t("No email is sent."),
      ]
        .filter(Boolean)
        .join(" "),
      confirmText: warnings.length ? t("Book anyway") : t("Book"),
      cancelText: t("Back"),
      variant: warnings.length ? "danger" : "confirmation",
    })
    if (!ok) return
    book.mutate({
      kind,
      source,
      orderId,
      orderNumber: orderNumber.trim() || undefined,
      ...person,
      meetup: kind === "MEETUP" ? meet : undefined,
      home:
        kind === "HOME"
          ? {
              date: home.date,
              windowStart: home.windowStart || undefined,
              windowEnd: home.windowEnd || undefined,
              addressLine: address.addressLine,
              addressArea: address.addressArea,
              postalCode: postal,
              addressFloor: address.addressFloor || undefined,
              addressNotes: address.addressNotes || undefined,
              availableFrom: avail.from || undefined,
              availableUntil: avail.until || undefined,
              availabilityNotes: avail.notes || undefined,
            }
          : undefined,
      notify: notify && Boolean(person.email.trim()),
    })
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-y-4">
      <div className="flex gap-2">
        {(["MEETUP", "HOME"] as const).map((k) => (
          <Chip key={k} active={kind === k} onClick={() => setKind(k)}>{KIND_LABEL[k]}</Chip>
        ))}
      </div>

      {kind === "MEETUP" && (
        <div className="flex flex-col gap-y-2">
          <Text size="small" weight="plus">{t("When")}</Text>
          {openTimes.length > 0 && (
            <>
              <Text size="xsmall" className="text-ui-fg-subtle">{t("Published times with room, or type the time you arranged below.")}</Text>
              <div className="-mx-4 flex gap-1.5 hc-hscroll px-4 pb-1 lg:mx-0 lg:flex-wrap lg:px-0">
                {openTimes.slice(0, 40).map((slot) => (
                  <Chip key={`${slot.date}|${slot.time}`} active={meet.date === slot.date && meet.time === slot.time} onClick={() => setMeet({ date: slot.date, time: slot.time })}>
                    {dayLabel(slot.date)} · {slot.time}
                  </Chip>
                ))}
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Field id="bk-meet-date" label={t("Day")}>
              <Input id="bk-meet-date" type="date" min={today} value={meet.date} onChange={(e) => setMeet({ ...meet, date: e.target.value })} />
            </Field>
            <Field id="bk-meet-time" label={t("Time")}>
              <Input id="bk-meet-time" type="time" step={300} value={meet.time} onChange={(e) => setMeet({ ...meet, time: e.target.value })} />
            </Field>
          </div>
          {arrangedTime && <Text size="xsmall" className="text-ui-fg-subtle">{t("Not a published time: it is made for this booking only.")}</Text>}
        </div>
      )}

      {kind === "HOME" && (
        <>
          <div className="flex flex-col gap-y-2">
            <Text size="small" weight="plus">{t("Delivery day")}</Text>
            {homeDays.length > 0 && (
              <div className="-mx-4 flex gap-1.5 hc-hscroll px-4 pb-1 lg:mx-0 lg:flex-wrap lg:px-0">
                {homeDays.map((d) => (
                  <Chip key={d.date} active={home.date === d.date} onClick={() => setHome({ ...home, date: d.date })}>
                    {dayLabel(d.date)} · {d.windowStart}–{d.windowEnd} · {d.stops.length}/{d.maxStops}
                  </Chip>
                ))}
              </div>
            )}
            <Field id="bk-home-date" label={homeDays.length ? t("Or another day") : t("Day")}>
              <Input id="bk-home-date" type="date" min={today} value={home.date} onChange={(e) => setHome({ ...home, date: e.target.value })} />
            </Field>
            {home.date && !existingDay && (
              <div className="flex flex-col gap-y-1.5">
                <Text size="xsmall" className="text-ui-fg-subtle">
                  {t("No delivery day on {day} yet: one is made for this booking only. It runs with this one stop and customers do not see it. Its hours (empty = the usual):", { day: dayLabel(home.date) })}
                </Text>
                <div className="grid grid-cols-2 gap-2">
                  <Input aria-label={t("Window from")} type="time" value={home.windowStart} onChange={(e) => setHome({ ...home, windowStart: e.target.value })} />
                  <Input aria-label={t("Window until")} type="time" value={home.windowEnd} onChange={(e) => setHome({ ...home, windowEnd: e.target.value })} />
                </div>
              </div>
            )}
            {existingDay && existingDay.status !== "OPEN" && (
              <Text size="xsmall" className="text-ui-fg-subtle">{t("That day is already locked in; the route is worked out again with this stop within 10 minutes.")}</Text>
            )}
            {existingDay && existingDay.status === "OPEN" && (
              <Text size="xsmall" className="text-ui-fg-subtle">
                {t("A published day: it runs only if it has {min} stops by its cutoff. If it does not, this booking comes back under Needs a call.", { min: existingDay.minStops })}
              </Text>
            )}
          </div>

          <div className="flex flex-col gap-y-2">
            <Text size="small" weight="plus">{t("Address")}</Text>
            <Field id="bk-street" label={t("Street and number")}>
              <Input id="bk-street" autoComplete="off" value={address.addressLine} onChange={(e) => setAddress({ ...address, addressLine: e.target.value })} placeholder="Ιπποκράτους 52" />
            </Field>
            <div className="grid grid-cols-[1fr_7rem] gap-2">
              <Field id="bk-area" label={t("Area")}>
                <Input id="bk-area" autoComplete="off" value={address.addressArea} onChange={(e) => setAddress({ ...address, addressArea: e.target.value })} placeholder="Αθήνα" />
              </Field>
              <Field id="bk-postal" label={t("Postcode")}>
                <Input id="bk-postal" inputMode="numeric" maxLength={6} value={address.postalCode} onChange={(e) => setAddress({ ...address, postalCode: e.target.value })} placeholder="10680" />
              </Field>
            </div>
            <div className="grid grid-cols-[7rem_1fr] gap-2">
              <Field id="bk-floor" label={t("Floor")}>
                <Input id="bk-floor" value={address.addressFloor} onChange={(e) => setAddress({ ...address, addressFloor: e.target.value })} placeholder="3ος" />
              </Field>
              <Field id="bk-notes" label={t("Doorbell, directions")}>
                <Input id="bk-notes" value={address.addressNotes} onChange={(e) => setAddress({ ...address, addressNotes: e.target.value })} placeholder="Κουδούνι: Παπαδόπουλος" />
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="small" variant="secondary" isLoading={geocode.isPending} disabled={address.addressLine.trim().length < 3} onClick={() => geocode.mutate()}>
                <MapPin /> {t("Check on the map")}
              </Button>
              {!checked && <Text size="xsmall" className="text-ui-fg-muted">{t("Checked again on Book anyway.")}</Text>}
            </div>
            {checked && <GeocodeResult result={checked} postalCode={postal} />}
          </div>

          <div className="flex flex-col gap-y-2">
            <Text size="small" weight="plus">{t("When they are home (optional)")}</Text>
            <div className="grid grid-cols-2 gap-2">
              <Input aria-label={t("Home from")} type="time" value={avail.from} onChange={(e) => setAvail({ ...avail, from: e.target.value })} />
              <Input aria-label={t("Home until")} type="time" value={avail.until} onChange={(e) => setAvail({ ...avail, until: e.target.value })} />
            </div>
            <Textarea rows={2} aria-label={t("Notes about when")} value={avail.notes} onChange={(e) => setAvail({ ...avail, notes: e.target.value })} placeholder={t("e.g. after 19:00, call first")} />
          </div>
        </>
      )}

      <div className="flex flex-col gap-y-2">
        <Text size="small" weight="plus">{t("Customer")}</Text>
        <div className="grid grid-cols-2 gap-2">
          <Field id="bk-first" label={t("First name")}>
            <Input id="bk-first" value={person.firstName} onChange={(e) => setPerson({ ...person, firstName: e.target.value })} />
          </Field>
          <Field id="bk-last" label={t("Last name")}>
            <Input id="bk-last" value={person.lastName} onChange={(e) => setPerson({ ...person, lastName: e.target.value })} />
          </Field>
          <Field id="bk-phone" label={t("Phone")}>
            <Input id="bk-phone" type="tel" value={person.phone} onChange={(e) => setPerson({ ...person, phone: e.target.value })} />
          </Field>
          <Field id="bk-ig" label="Instagram">
            <Input id="bk-ig" value={person.instagram} onChange={(e) => setPerson({ ...person, instagram: e.target.value })} placeholder={t("handle")} />
          </Field>
        </div>
        <Field id="bk-email" label={t("Email")}>
          <Input id="bk-email" type="email" value={person.email} onChange={(e) => setPerson({ ...person, email: e.target.value })} />
        </Field>
        {source === "hq" && (
          <Field id="bk-order" label={t("Order")} hint={t("The order number, or what it is for.")}>
            <Input id="bk-order" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder={t("e.g. #1650 or Instagram sale")} />
          </Field>
        )}
      </div>

      <div className="flex items-start gap-x-2">
        <Checkbox id="bk-notify" checked={notify && Boolean(person.email.trim())} disabled={!person.email.trim()} onCheckedChange={(value) => setNotify(value === true)} className="mt-0.5" />
        <Label htmlFor="bk-notify" className="txt-compact-small">
          {person.email.trim()
            ? kind === "HOME"
              ? t("Email {email} the booking (Greek, with the day, time and address)", { email: person.email.trim() })
              : t("Email {email} the booking (Greek, with the day, time and meeting point)", { email: person.email.trim() })
            : t("No email known, so nobody is emailed")}
        </Label>
      </div>

      {missing.length > 0 && <Text size="xsmall" className="text-ui-fg-muted">{t("Still needed: {list}.", { list: missing.join(", ") })}</Text>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" isLoading={book.isPending} disabled={missing.length > 0}>{t("Book")}</Button>
        {props.onCancel && <Button type="button" variant="secondary" onClick={props.onCancel}>{t("Cancel")}</Button>}
      </div>
    </form>
  )
}

/**
 * On an order's page: its delivery booking, or — when the customer has not booked — the button to
 * book them on the day and time arranged with them, and (site orders) their own link to send.
 */
export function OrderBooking(props: {
  orderKey: string
  source: "site" | "custom"
  orderId?: string
  orderNumber?: string
  /** How the customer knows the order, for the DM: "#12", "#1650". */
  customerRef: string
  prefill: BookingPrefill
}) {
  const { orderKey } = props
  const [open, setOpen] = useState(false)
  const status = useQuery({ queryKey: ["booking", "orders", orderKey], queryFn: () => bookingApi.orders([orderKey]), retry: false })
  const booking = status.data?.bookings[orderKey]
  const link = status.data?.links[orderKey]
  // The customer's message is Greek whatever language HQ is in.
  const { message } = useMessageTemplates()
  const dm = link ? message("booking.dm", { order: props.customerRef, link }) : ""

  return (
    <Container className="flex flex-col gap-y-3">
      <div className="flex items-center justify-between gap-x-2">
        <Heading level="h2">{t("Delivery booking")}</Heading>
        {booking && <Badge size="2xsmall" color={STATUS_COLOUR[booking.status]}>{STATUS_LABEL[booking.status]}</Badge>}
      </div>
      {status.isLoading && <Skeleton className="h-10 w-full rounded-md" />}
      {status.isError && <Text size="small" className="text-ui-fg-muted">{errorMessage(status.error)}</Text>}
      {booking && (
        <Link to={`/booking/b/${booking.id}`} className="bg-ui-bg-subtle hover:bg-ui-bg-subtle-hover flex items-center justify-between gap-x-3 rounded-md p-3">
          <div className="min-w-0">
            <Text size="small" weight="plus">{KIND_LABEL[booking.kind]} · {whenLabel(booking)}</Text>
            <Text size="xsmall" className="text-ui-fg-subtle">{t("{ref} · move or cancel it there", { ref: booking.ref })}</Text>
          </div>
          <ArrowUpRightOnBox className="text-ui-fg-muted shrink-0" />
        </Link>
      )}
      {status.data && !booking && !open && (
        <>
          <Text size="small" className="text-ui-fg-subtle">
            {[
              t("Not booked yet."),
              props.source === "site" ? t("The customer has a booking link on their receipt and in their email.") : "",
              t("Once you have agreed a day and time with them, book it here."),
            ]
              .filter(Boolean)
              .join(" ")}
          </Text>
          <div className="flex flex-wrap gap-2">
            <Button size="small" onClick={() => setOpen(true)}>{t("Book a day and time")}</Button>
            {link && (
              <Button size="small" variant="secondary" onClick={() => void copyText(dm)}>{t("Copy a DM with their link")}</Button>
            )}
          </div>
        </>
      )}
      {open && (
        <BookDeliveryForm
          source={props.source}
          orderId={props.orderId}
          orderNumber={props.orderNumber}
          prefill={props.prefill}
          onDone={() => setOpen(false)}
          onCancel={() => setOpen(false)}
        />
      )}
    </Container>
  )
}
